import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, Save, Lock, Phone, User, 
  Shield, AlertTriangle, CheckCircle
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const OwnerSettings: React.FC = () => {
  const navigate = useNavigate();
  const { ownerCredentials, updateOwnerCredentials } = useAuth();
  
  const [phone, setPhone] = useState(ownerCredentials.phone);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleUpdateCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    // Validate current password
    if (currentPassword !== ownerCredentials.password) {
      setMessage({ type: 'error', text: 'كلمة المرور الحالية غير صحيحة' });
      return;
    }

    // Validate new password
    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'كلمة المرور الجديدة يجب أن تكون 8 أحرف على الأقل' });
      return;
    }

    // Validate password confirmation
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'كلمتا المرور الجديدتان غير متطابقتين' });
      return;
    }

    setIsLoading(true);

    try {
      // Update credentials
      updateOwnerCredentials(phone, newPassword);
      setMessage({ type: 'success', text: 'تم تحديث بيانات الدخول بنجاح' });
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setMessage({ type: 'error', text: 'حدث خطأ أثناء التحديث' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/owner/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold">إعدادات المالك</h1>
        </div>
      </header>

      <main className="p-6 max-w-2xl mx-auto">
        {/* Security Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-yellow-800 mb-1">تنبيه أمني</h3>
              <p className="text-yellow-700 text-sm">
                هذه الإعدادات حساسة وتؤثر على وصولك للنظام. تأكد من حفظ بيانات الدخول الجديدة في مكان آمن.
              </p>
            </div>
          </div>
        </div>

        {/* Update Form */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="font-bold text-gray-800">بيانات الدخول</h2>
              <p className="text-gray-500 text-sm">تحديث رقم الهاتف وكلمة المرور</p>
            </div>
          </div>

          {message && (
            <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
              message.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-700' 
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              )}
              <p>{message.text}</p>
            </div>
          )}

          <form onSubmit={handleUpdateCredentials} className="space-y-5">
            {/* Phone */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                رقم الهاتف الجديد
              </label>
              <div className="relative">
                <Phone className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl py-3 pr-12 pl-4 focus:outline-none focus:border-primary-500 transition-colors"
                  placeholder="أدخل رقم الهاتف الجديد"
                  dir="ltr"
                />
              </div>
            </div>

            <hr className="border-gray-100" />

            {/* Current Password */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                كلمة المرور الحالية
              </label>
              <div className="relative">
                <Lock className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl py-3 pr-12 pl-4 focus:outline-none focus:border-primary-500 transition-colors"
                  placeholder="أدخل كلمة المرور الحالية"
                  dir="ltr"
                />
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                كلمة المرور الجديدة
              </label>
              <div className="relative">
                <Lock className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl py-3 pr-12 pl-4 focus:outline-none focus:border-primary-500 transition-colors"
                  placeholder="أدخل كلمة المرور الجديدة"
                  dir="ltr"
                />
              </div>
              <p className="text-gray-400 text-xs mt-1">8 أحرف على الأقل</p>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                تأكيد كلمة المرور الجديدة
              </label>
              <div className="relative">
                <Lock className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl py-3 pr-12 pl-4 focus:outline-none focus:border-primary-500 transition-colors"
                  placeholder="أعد إدخال كلمة المرور الجديدة"
                  dir="ltr"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !phone || !currentPassword || !newPassword || !confirmPassword}
              className="w-full bg-primary-500 text-white font-semibold py-4 rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>حفظ التغييرات</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Current Credentials Info */}
        <div className="bg-gray-100 rounded-2xl p-6 mt-6">
          <h3 className="font-medium text-gray-700 mb-3">بيانات الدخول الحالية</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-400" />
              <span className="text-gray-500">الهاتف:</span>
              <span className="font-medium" dir="ltr">{ownerCredentials.phone}</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-gray-400" />
              <span className="text-gray-500">كلمة المرور:</span>
              <span className="font-medium">********</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OwnerSettings;
